import { GuardianPayload } from '_api/types';
import { ACCOUNT_TYPE } from '_constants';
import {
  LocalizedDatePicker,
  InputField,
  SelectMultipleField,
  SelectField,
  TextDivider,
} from 'components/molecules/formFields';
import { FormBuilder } from 'components/organisms/forms/form-builder';
import React, { useMemo, useState } from 'react';
import { usePatientForm, useTherapists, useUser } from 'utils/hooks';

import { AddPatientFormType, BaseFormProps } from './types';

export const AddPatientForm = ({
  disableAll = false,
  initValue,
  onBack,
  onSubmit,
}: BaseFormProps<GuardianPayload>) => {
  const { diagnosesOptions, ethnicityOptions, genderOptions, raceOptions, sexOptions } =
    usePatientForm();
  const { isLoading, therapistOptions } = useTherapists();
  const [formValue, setFormValue] = useState<AddPatientFormType>({
    ...initValue,
    patientfirstName: initValue.patient.firstName,
    patientlastName: initValue.patient.lastName,
    patientdateOfBirth: initValue.patient.dateOfBirth,
    patientdiagnosis: initValue.patient.diagnosis,
    patienttherapistIds: initValue.patient.therapistIds as string[],
    patientmrn: initValue.patient.mrn,
  });
  const [value, setValue] = useState(initValue);
  const { account } = useUser();
  const isTherapistOrGuardian = useMemo(
    () =>
      account?.accountType === ACCOUNT_TYPE.THERAPIST ||
      account?.accountType === ACCOUNT_TYPE.GUARDIAN,
    [account],
  );
  const updateForm = async (key: any, value: any) => {
    setFormValue((current) => ({ ...current, [key]: value }));
    if (key.includes('patient')) {
      const patientKey = key.replace('patient', '');
      setValue(({ patient: currentPatient, ...guardian }) => ({
        ...guardian,
        patient: {
          ...currentPatient,
          [patientKey]: value,
        },
      }));
    } else {
      setValue((current) => ({
        ...current,
        [key]: value,
      }));
    }
  };

  const generalFormFields = [
    {
      component: TextDivider,
      label: 'Child Details',
      fieldKey: 'childDetails-text',
      xs: 12,
      gridItemCss: { marginTop: '10px' },
    },
    {
      component: InputField,
      fieldKey: 'patientfirstName',
      label: 'FIRST NAME',
      required: true,
      xs: 3,
    },
    {
      component: InputField,
      fieldKey: 'patientlastName',
      label: 'LAST NAME',
      required: true,
      xs: 3,
    },
    {
      component: LocalizedDatePicker,
      fieldKey: 'patientdateOfBirth',
      label: 'DATE OF BIRTH',
      required: true,
      xs: 3,
    },
    {
      component: InputField,
      fieldKey: 'patientmrn',
      label: 'MRN',
      required: false,
      xs: 3,
    },
    {
      component: SelectField,
      fieldKey: 'patientsex',
      label: 'SEX',
      options: sexOptions,
      disabled: true,
      xs: 3,
    },
    {
      component: SelectField,
      fieldKey: 'patientgender',
      label: 'GENDER IDENTITY',
      options: genderOptions,
      disabled: true,
      xs: 3,
    },
    {
      component: SelectField,
      fieldKey: 'patientrace',
      label: 'RACE',
      options: raceOptions,
      disabled: true,
      xs: 3,
    },
    {
      component: SelectField,
      fieldKey: 'patientethnicity',
      label: 'ETHNICITY',
      options: ethnicityOptions,
      disabled: true,
      xs: 3,
    },
    {
      component: SelectMultipleField,
      fieldKey: 'patientdiagnosis',
      label: 'DIAGNOSIS',
      options: diagnosesOptions,
      required: true,
      xs: 6,
    },
  ];

  const guardianFormFields = [
    {
      component: TextDivider,
      label: 'Guardian Details',
      fieldKey: 'guardianDetails-text',
      xs: 12,
      gridItemCss: { marginTop: '10px' },
    },
    {
      component: InputField,
      label: 'FIRST NAME',
      fieldKey: 'firstName',
      required: true,
      xs: 6,
    },
    {
      component: InputField,
      label: 'LAST NAME',
      fieldKey: 'lastName',
      required: true,
      xs: 6,
    },
    {
      component: InputField,
      label: 'PHONE NUMBER',
      fieldKey: 'phoneNumber',
      type: 'number',
      mask: '(000) 000-0000',
      required: true,
      xs: 6,
    },
    {
      component: InputField,
      label: 'EMAIL ADDRESS',
      fieldKey: 'email',
      required: true,
      type: 'email',
      xs: 6,
    },
  ];

  const therapistFormField = [
    {
      component: SelectMultipleField,
      fieldKey: 'patienttherapistIds',
      label: 'THERAPIST',
      options: therapistOptions,
      required: true,
      xs: 6,
    },
  ];
  const formFields = isTherapistOrGuardian
    ? [...generalFormFields, ...guardianFormFields]
    : [...generalFormFields, ...therapistFormField, ...guardianFormFields];

  return !isLoading ? (
    <FormBuilder
      backLabel="Cancel"
      disabled={disableAll}
      initValues={formValue}
      formFields={formFields}
      onBack={onBack}
      onSubmit={() => onSubmit(value)}
      onUpdate={updateForm}
    />
  ) : null;
};
